import { inject, Injectable } from '@angular/core';
import { HttpService } from '@trovata/app/core/services/http.service';
import { Observable } from 'rxjs';
import {
	StageFeedback,
	PaymentToCreate,
	Payment,
	PaymentSubmissionOrCancelation,
	BatchPayment,
	GetBatchPaymentsResponse,
	GetPaymentsV1Response,
	ValidateBatchCsvFileResponse,
	PaymentsListFilters,
	LexisNexisBankInformationResponse,
	InstitutionCountry,
	InstitutionAddtlInstruction,
	PaymentsListQuery,
	GetPaymentRecordsResponse,
	BulkApprovalRequest,
	BulkApprovalResponse,
} from '@trovata/app/features/payments/models/payment.model';
import { environment } from '@trovata/environments/environment';
import { HttpParams } from '@angular/common/http';
import { DateTime } from 'luxon';

/**
 * PaymentsHttpService - HTTP service for payments API operations.
 *
 * Provides methods for CRUD operations on payments and batch payments,
 * including stage feedback, approvals, cancellations, and file attachments.
 *
 * Key behaviors:
 * - Handles date range padding to include full end dates in filters
 * - Supports both v1 and v2 payments list APIs
 * - Provides batch payment creation and validation endpoints
 *
 * Note: This is a low-level HTTP service. For state management and
 * business logic, use PaymentsFacadeService instead.
 */
@Injectable({
	providedIn: 'root',
})
export class PaymentsHttpService {
	// ==========================================
	// Services (injected)
	// ==========================================

	/** HTTP service for making API requests */
	private readonly httpService: HttpService = inject(HttpService);

	// ==========================================
	// Payment List & Query Methods
	// ==========================================

	/**
	 * Retrieves paginated payment records using the v2 API.
	 *
	 * @param body - Query parameters including filters, sorting, and pagination
	 * @returns Observable of payment records response with total count
	 */
	getPaymentRecords(body: PaymentsListQuery): Observable<GetPaymentRecordsResponse> {
		// Deep clone using native structuredClone to avoid mutating the original - prevents the date picker from displaying the padded date
		const clonedBody: PaymentsListQuery = structuredClone(body);
		// Pad the end date by one day because the API uses exclusive end dates (< endDate).
		// Without padding, a query for "Jan 1-31" would exclude Jan 31 entirely.
		clonedBody.initiated = this.padEndDate(clonedBody.initiated);
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v2/payments-list`, clonedBody);
	}

	/**
	 * Retrieves paginated payments using the v1 API.
	 *
	 * @param body - Filter parameters for the payments query
	 * @returns Observable of payments response with payment list
	 */
	getPaginatedPayments(body: PaymentsListFilters): Observable<GetPaymentsV1Response> {
		// Deep clone using native structuredClone to avoid mutating the original - prevents the date picker from displaying the padded date
		const clonedBody: PaymentsListFilters = structuredClone(body);
		// Pad the end date by one day because the API uses exclusive end dates (< endDate).
		// Without padding, a query for "Jan 1-31" would exclude Jan 31 entirely.
		clonedBody.initiated = this.padEndDate(clonedBody.initiated);
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/payments-list`, clonedBody);
	}

	/**
	 * Retrieves a single payment by its ID.
	 *
	 * @param paymentId - The unique identifier of the payment
	 * @returns Observable of payments response containing the requested payment
	 */
	getPaymentById(paymentId: string): Observable<GetPaymentsV1Response> {
		return this.httpService.get(`${environment.workspaceAPI('payments')}/v1/payments?paymentId=${paymentId}`);
	}

	/**
	 * Retrieves a batch payment by its ID.
	 *
	 * @param batchId - The unique identifier of the batch payment
	 * @returns Observable of batch payments response containing the requested batch
	 */
	getBatchPaymentById(batchId: string): Observable<GetBatchPaymentsResponse> {
		return this.httpService.get(`${environment.workspaceAPI('payments')}/v1/batches?batchId=${batchId}`);
	}

	/**
	 * Creates a new payment.
	 *
	 * @param body - The payment data to create
	 * @returns Observable of the created payment
	 */
	createPayment(body: PaymentToCreate): Observable<Payment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/payment`, body);
	}

	// ==========================================
	// Stage Feedback & Approval Methods
	// ==========================================

	/**
	 * Sets stage feedback (approve/reject) for a payment.
	 *
	 * @param body - The stage feedback data including payment ID and decision
	 * @returns Observable of the updated payment
	 */
	setStageFeedback(body: StageFeedback): Observable<Payment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/payment/stage/feedback`, body);
	}

	/**
	 * Sets stage feedback (approve/reject) for a batch payment.
	 *
	 * @param body - The stage feedback data including batch ID and decision
	 * @returns Observable of the updated batch payment
	 */
	setBatchStageFeedback(body: StageFeedback): Observable<BatchPayment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/batches/stage/feedback`, body);
	}

	/**
	 * Submits bulk approval/rejection requests for multiple payments and batches.
	 *
	 * This endpoint uses a non-fail-fast pattern: all items are processed,
	 * and the response contains both successful results and errors.
	 * HTTP status is always 200 even if some items fail.
	 *
	 * @param request - Bulk approval request with approvals array and optional MFA credentials
	 * @returns Observable of the approval response with successes and errors
	 */
	submitBulkApproval(request: BulkApprovalRequest): Observable<BulkApprovalResponse> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/payments/approval`, request);
	}

	/**
	 * Cancels a payment.
	 *
	 * @param body - The cancellation request data including payment ID
	 * @returns Observable of the cancelled payment
	 */
	cancelPayment(body: PaymentSubmissionOrCancelation): Observable<Payment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/payment/cancelation`, body);
	}

	/**
	 * Cancels a batch payment.
	 *
	 * @param body - The cancellation request data including batch ID
	 * @returns Observable of the cancelled batch payment
	 */
	cancelBatchPayment(body: PaymentSubmissionOrCancelation): Observable<BatchPayment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/batches/cancelation`, body);
	}

	// ==========================================
	// Batch Payment Methods
	// ==========================================

	/**
	 * Creates a new batch payment from uploaded file data.
	 *
	 * @param body - FormData containing the batch payment file and metadata
	 * @returns Observable of the created batch payment
	 */
	createBatchPayment(body: FormData): Observable<BatchPayment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/batches`, body);
	}

	/**
	 * Retrieves all payments belonging to a batch.
	 *
	 * @param batchId - The unique identifier of the batch payment
	 * @returns Observable of payments response containing batch child payments
	 */
	getPaymentsByBatchId(batchId: string): Observable<GetPaymentsV1Response> {
		return this.httpService.get(`${environment.workspaceAPI('payments')}/v1/payments?batchId=${batchId}`);
	}

	/**
	 * Validates a batch payment CSV file before upload.
	 *
	 * @param body - FormData containing the CSV file to validate
	 * @returns Observable of validation response with any errors or warnings
	 */
	validateBatchCsvFile(body: FormData): Observable<ValidateBatchCsvFileResponse> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/batches/validation/file`, body);
	}

	// ==========================================
	// Institution Methods
	// ==========================================

	/**
	 * Retrieves bank information using either BIC or routing number.
	 *
	 * @param isNumberBic - If true, searches by BIC (SWIFT code); if false, searches by routing number (ABA)
	 * @param accountNumberOrBic - The BIC code (e.g., "CHASUS33") or routing number (e.g., "021000021") to search
	 * @param countryCode - The ISO country code for the institution (e.g., "US")
	 * @returns Observable of bank information response from LexisNexis
	 */
	getBankInformationByAccountNumber(isNumberBic: boolean, accountNumberOrBic: string, countryCode: string): Observable<LexisNexisBankInformationResponse> {
		const bankIdentifier: string = isNumberBic ? 'bic' : 'routingNumber';
		return this.httpService.get(
			`${environment.workspaceAPI('payments')}/v1/institutions/metadata?${bankIdentifier}=${accountNumberOrBic}&countryCode=${countryCode}`
		);
	}

	/**
	 * Retrieves additional instructions for an institution.
	 *
	 * @param countryCode - The ISO country code (e.g., "US", "GB")
	 * @param institution - The institution identifier (e.g., "chase", "bofa")
	 * @param paymentType - The type of payment (e.g., "wire", "ach")
	 * @returns Observable of additional instructions for the institution
	 */
	getInstitutionInstructions(countryCode: string, institution: string, paymentType: string): Observable<InstitutionAddtlInstruction[]> {
		const params: HttpParams = new HttpParams({ fromObject: { country: countryCode, institution: institution, paymentType: paymentType } });
		return this.httpService.get(`${environment.workspaceAPI('payments')}/v1/institutions/instructions`, { params: params });
	}

	/**
	 * Retrieves supported countries for an institution.
	 *
	 * @param institution - The institution identifier (e.g., "chase", "bofa")
	 * @param paymentType - The type of payment (e.g., "wire", "ach")
	 * @returns Observable of countries supported by the institution
	 */
	getInstitutionCountries(institution: string, paymentType: string): Observable<InstitutionCountry[]> {
		const params: HttpParams = new HttpParams({ fromObject: { institution: institution, paymentType: paymentType } });
		return this.httpService.get(`${environment.workspaceAPI('payments')}/v1/institutions/countries`, { params: params });
	}

	// ==========================================
	// File Attachment Methods
	// ==========================================

	/**
	 * Uploads a file attachment to a payment.
	 *
	 * @param body - FormData containing the file to upload
	 * @param paymentId - The unique identifier of the payment
	 * @returns Observable of the updated payment with attachment
	 */
	uploadPaymentFile(body: FormData, paymentId: string): Observable<Payment> {
		return this.httpService.post(`${environment.workspaceAPI('payments')}/v1/payments/${paymentId}/attachments`, body);
	}

	/**
	 * Deletes a file attachment from a payment.
	 *
	 * @param attachmentId - The unique identifier of the attachment to delete
	 * @param paymentId - The unique identifier of the payment
	 * @returns Observable of the updated payment without the attachment
	 */
	deleteAttachmentFromPayment(attachmentId: string, paymentId: string): Observable<Payment> {
		return this.httpService.delete(`${environment.workspaceAPI('payments')}/v1/payments/${paymentId}/attachments?attachmentId=${attachmentId}`);
	}

	// ==========================================
	// Private Methods
	// ==========================================

	/**
	 * Compensates for the API's exclusive end date behavior by adding one day to the end date.
	 *
	 * @param initiated - The initiated filter string, optionally a date range (e.g., "between:2025-01-01,2025-01-31")
	 * @returns The initiated filter with the end date padded by one day (e.g., "between:2025-01-01,2025-02-01")
	 */
	private padEndDate(initiated: string | undefined): string | undefined {
		// Guard clause: only process 'between:' date range filters; other filter types pass through unchanged
		if (initiated && initiated.startsWith('between:')) {
			const dateRange: string = initiated.split(':')[1];
			const splitStartDate: string = dateRange.split(',')[0];
			const splitEndDate: string = dateRange.split(',')[1];
			if (splitEndDate) {
				const paddedEndDate: string | null = DateTime.fromISO(splitEndDate).plus({ days: 1 }).toISODate();
				if (paddedEndDate) {
					return `between:${splitStartDate},${paddedEndDate}`;
				}
			}
		}
		return initiated;
	}
}
